import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";
import {
  base64UrlToBytes,
  bytesToBase64Url,
  parseControlMessage,
  randomBase64Url,
  type FileDecision,
  type FileOffer,
  type TransferControlMessage,
  type TransferManifest,
} from "@kitae/transfer-protocol";
import type { PreparedFile } from "./files";

const chunkSize = 16 * 1024;
const bufferedHighWaterMark = 4 * 1024 * 1024;
const maximumUnacknowledgedBytes = 4 * 1024 * 1024;
const writeBatchSize = 512 * 1024;
const partialBatchDelayMs = 40;
const controlFragmentLength = 8_000;
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

export interface TransferProgress {
  readonly fileId: string;
  readonly transferred: number;
  readonly total: number;
  readonly completed?: boolean;
}

export function encodeControlFrames(message: TransferControlMessage): string[] {
  const serialized = JSON.stringify(message);
  if (textEncoder.encode(serialized).byteLength <= controlFragmentLength) {
    return [serialized];
  }
  const encoded = bytesToBase64Url(textEncoder.encode(serialized));
  const id = randomBase64Url(6);
  const total = Math.ceil(encoded.length / controlFragmentLength);
  const frames: string[] = [];
  for (let index = 0; index < total; index += 1) {
    frames.push(`KT1:${id}:${index}:${total}:${encoded.slice(index * controlFragmentLength, (index + 1) * controlFragmentLength)}`);
  }
  return frames;
}

function sendControl(channel: RTCDataChannel, message: TransferControlMessage): void {
  for (const frame of encodeControlFrames(message)) channel.send(frame);
}

export class ControlDecoder {
  readonly #fragments = new Map<string, { readonly total: number; readonly chunks: string[] }>();

  decode(value: string): TransferControlMessage | undefined {
    if (!value.startsWith("KT1:")) return parseControlMessage(value);
    const [prefix, id, indexRaw, totalRaw, chunk] = value.split(":");
    const index = Number(indexRaw);
    const total = Number(totalRaw);
    if (prefix !== "KT1" || !id || !chunk || !Number.isSafeInteger(index) || !Number.isSafeInteger(total) || index < 0 || total < 1 || total > 2_000 || index >= total) {
      throw new Error("Fragment de contrôle invalide");
    }
    const entry = this.#fragments.get(id) ?? { total, chunks: new Array<string>(total) };
    if (entry.total !== total) throw new Error("Suite de fragments incohérente");
    entry.chunks[index] = chunk;
    this.#fragments.set(id, entry);
    if (entry.chunks.filter(Boolean).length !== total) return undefined;
    this.#fragments.delete(id);
    return parseControlMessage(textDecoder.decode(base64UrlToBytes(entry.chunks.join(""))));
  }
}

async function waitForBuffer(channel: RTCDataChannel): Promise<void> {
  if (channel.bufferedAmount < bufferedHighWaterMark) return;
  channel.bufferedAmountLowThreshold = bufferedHighWaterMark / 2;
  await new Promise<void>((resolve) => {
    const finish = () => {
      channel.removeEventListener("bufferedamountlow", finish);
      channel.removeEventListener("close", finish);
      resolve();
    };
    channel.addEventListener("bufferedamountlow", finish, { once: true });
    channel.addEventListener("close", finish, { once: true });
  });
}

export function shouldPauseForAcknowledgement(sentOffset: number, acknowledgedOffset: number): boolean {
  return sentOffset - acknowledgedOffset >= maximumUnacknowledgedBytes;
}

async function waitUntil(predicate: () => boolean, timeoutMs = 30_000): Promise<boolean> {
  const started = performance.now();
  while (!predicate()) {
    if (performance.now() - started > timeoutMs) return false;
    await new Promise((resolve) => setTimeout(resolve, 12));
  }
  return true;
}

export class SenderTransfer {
  #channel?: RTCDataChannel;
  #prepared: readonly PreparedFile[] = [];
  #manifest?: TransferManifest;
  #decisions?: readonly FileDecision[];
  readonly #offsets = new Map<string, number>();
  readonly #hashes = new Map<string, { hash: ReturnType<typeof sha256.create>; offset: number; digest?: string }>();
  readonly #completed = new Set<string>();
  #sending = false;
  #cancelled = false;
  readonly #controlDecoder = new ControlDecoder();
  onProgress?: (progress: TransferProgress) => void;
  onComplete?: () => void;
  onError?: (message: string) => void;

  setPreparedFiles(prepared: readonly PreparedFile[]): TransferManifest {
    this.#prepared = prepared;
    this.#decisions = undefined;
    this.#offsets.clear();
    this.#completed.clear();
    this.#hashes.clear();
    this.#cancelled = false;
    this.#manifest = {
      transferId: randomBase64Url(12),
      files: prepared.map(({ offer }) => offer),
      totalSize: prepared.reduce((total, { file }) => total + file.size, 0),
    };
    if (this.#channel?.readyState === "open") sendControl(this.#channel, { type: "manifest", manifest: this.#manifest });
    return this.#manifest;
  }

  resetBatch(): void {
    this.#prepared = [];
    this.#manifest = undefined;
    this.#decisions = undefined;
    this.#offsets.clear();
    this.#completed.clear();
    this.#hashes.clear();
    this.#cancelled = false;
  }

  attach(channel: RTCDataChannel): void {
    this.#channel = channel;
    channel.onmessage = (event) => this.#handleMessage(event.data);
    channel.onclose = () => undefined;
    if (this.#manifest) sendControl(channel, { type: "manifest", manifest: this.#manifest });
  }

  cancel(reason = "Transfert annulé"): void {
    this.#cancelled = true;
    if (this.#channel?.readyState === "open") sendControl(this.#channel, { type: "cancel", reason });
  }

  #handleMessage(data: unknown): void {
    if (typeof data !== "string") return;
    try {
      const message = this.#controlDecoder.decode(data);
      if (!message) return;
      if (message.type === "decision") {
        this.#decisions = message.decisions;
        void this.#sendAll();
      } else if (message.type === "ack") {
        this.#offsets.set(message.fileId, message.offset);
      } else if (message.type === "resume") {
        for (const [fileId, offset] of Object.entries(message.offsets)) this.#offsets.set(fileId, offset);
        void this.#sendAll();
      } else if (message.type === "file-complete") {
        this.#completed.add(message.fileId);
      } else if (message.type === "cancel" || message.type === "error") {
        this.#cancelled = true;
        this.onError?.(message.type === "error" ? message.message : message.reason ?? "Transfert refusé");
      }
    } catch {
      this.onError?.("Le destinataire a envoyé un message invalide.");
    }
  }

  async #sendAll(): Promise<void> {
    if (this.#sending || !this.#decisions) return;
    this.#sending = true;
    this.#cancelled = false;
    try {
      for (const decision of this.#decisions) {
        if (decision.action === "skip" || this.#completed.has(decision.id)) continue;
        const prepared = this.#prepared.find(({ offer }) => offer.id === decision.id);
        if (!prepared) continue;
        await this.#sendFile(prepared);
        if (this.#cancelled) return;
      }
      const channel = await this.#openChannel();
      if (!channel || this.#cancelled) return;
      sendControl(channel, { type: "transfer-complete" });
      this.onComplete?.();
    } catch (error) {
      this.onError?.(error instanceof Error ? error.message : "Le transfert a échoué.");
    } finally {
      this.#sending = false;
    }
  }

  async #sendFile(prepared: PreparedFile): Promise<void> {
    const { file, offer } = prepared;
    while (!this.#completed.has(offer.id) && !this.#cancelled) {
      const channel = await this.#openChannel();
      if (!channel) return;
      let acknowledgedOffset = this.#offsets.get(offer.id) ?? 0;
      let sentOffset = acknowledgedOffset;
      sendControl(channel, { type: "file-start", fileId: offer.id, offset: acknowledgedOffset });
      while (sentOffset < file.size && channel.readyState === "open" && !this.#cancelled) {
        if (shouldPauseForAcknowledgement(sentOffset, acknowledgedOffset)) {
          const previousOffset = acknowledgedOffset;
          const expectedChannel = channel;
          const progressed = await waitUntil(() =>
            (this.#offsets.get(offer.id) ?? 0) > previousOffset || this.#channel !== expectedChannel || expectedChannel.readyState !== "open" || this.#cancelled,
          );
          if (!progressed || this.#channel !== expectedChannel || expectedChannel.readyState !== "open") break;
          acknowledgedOffset = this.#offsets.get(offer.id) ?? previousOffset;
          this.onProgress?.({ fileId: offer.id, transferred: acknowledgedOffset, total: file.size });
          continue;
        }
        await waitForBuffer(channel);
        if (this.#channel !== channel || channel.readyState !== "open" || this.#cancelled) break;
        const next = Math.min(sentOffset + chunkSize, file.size);
        const bytes = new Uint8Array(await file.slice(sentOffset, next).arrayBuffer());
        if (this.#channel !== channel || channel.readyState !== "open" || this.#cancelled) break;
        this.#updateHash(offer.id, sentOffset, bytes);
        channel.send(bytes);
        sentOffset = next;
        const latestAcknowledgement = this.#offsets.get(offer.id) ?? acknowledgedOffset;
        if (latestAcknowledgement > acknowledgedOffset) {
          acknowledgedOffset = latestAcknowledgement;
          this.onProgress?.({ fileId: offer.id, transferred: acknowledgedOffset, total: file.size });
        }
      }
      if (sentOffset >= file.size && channel.readyState === "open") {
        const expectedChannel = channel;
        const acknowledged = await waitUntil(() =>
          (this.#offsets.get(offer.id) ?? 0) >= file.size || this.#channel !== expectedChannel || expectedChannel.readyState !== "open" || this.#cancelled,
        );
        if (!acknowledged || this.#channel !== expectedChannel || expectedChannel.readyState !== "open" || this.#cancelled) continue;
        acknowledgedOffset = this.#offsets.get(offer.id) ?? acknowledgedOffset;
        this.onProgress?.({ fileId: offer.id, transferred: acknowledgedOffset, total: file.size });
        const sha256 = this.#fileDigest(offer.id, file.size);
        sendControl(channel, { type: "file-end", fileId: offer.id, sha256 });
        const finished = await waitUntil(() => this.#completed.has(offer.id) || channel.readyState !== "open" || this.#cancelled);
        if (finished && this.#completed.has(offer.id)) {
          this.onProgress?.({ fileId: offer.id, transferred: file.size, total: file.size, completed: true });
          return;
        }
      }
    }
  }

  #updateHash(fileId: string, chunkOffset: number, bytes: Uint8Array): void {
    const state = this.#hashes.get(fileId) ?? { hash: sha256.create(), offset: 0 };
    const chunkEnd = chunkOffset + bytes.byteLength;
    if (chunkEnd > state.offset) {
      if (chunkOffset > state.offset) throw new Error("Flux de fichier non contigu");
      const unseenStart = Math.max(0, state.offset - chunkOffset);
      state.hash.update(bytes.subarray(unseenStart));
      state.offset = chunkEnd;
    }
    this.#hashes.set(fileId, state);
  }

  #fileDigest(fileId: string, size: number): string {
    const state = this.#hashes.get(fileId) ?? { hash: sha256.create(), offset: 0 };
    this.#hashes.set(fileId, state);
    if (!state || state.offset !== size) throw new Error("Empreinte du fichier incomplète");
    state.digest ??= bytesToHex(state.hash.digest());
    return state.digest;
  }

  async #openChannel(): Promise<RTCDataChannel | undefined> {
    const ready = await waitUntil(() => this.#channel?.readyState === "open" || this.#cancelled, 60_000);
    if (!ready || this.#cancelled) return undefined;
    return this.#channel;
  }
}

interface ReceiveState {
  readonly offer: FileOffer;
  readonly decision: FileDecision;
  readonly handle: FileSystemFileHandle;
  writer?: FileSystemWritableFileStream;
  hash: ReturnType<typeof sha256.create>;
  offset: number;
  pendingChunks: Uint8Array[];
  pendingBytes: number;
  flushTimer?: ReturnType<typeof setTimeout>;
}

export class ReceiverTransfer {
  #channel?: RTCDataChannel;
  #manifest?: TransferManifest;
  readonly #files = new Map<string, ReceiveState>();
  #activeFileId?: string;
  #writeQueue = Promise.resolve();
  #cancelled = false;
  readonly #controlDecoder = new ControlDecoder();
  onManifest?: (manifest: TransferManifest) => void;
  onProgress?: (progress: TransferProgress) => void;
  onComplete?: () => void;
  onError?: (message: string) => void;

  attach(channel: RTCDataChannel): void {
    this.#channel = channel;
    channel.onmessage = (event) => {
      this.#writeQueue = this.#writeQueue.then(() => this.#handleMessage(event.data)).catch((error) => {
        this.onError?.(error instanceof Error ? error.message : "Échec de l’écriture du fichier.");
      });
    };
    if (this.#files.size > 0) this.#queueResume(channel);
  }

  async accept(directory: FileSystemDirectoryHandle, decisions: readonly FileDecision[]): Promise<void> {
    if (!this.#manifest || !this.#channel) throw new Error("Aucun transfert à accepter");
    await this.resetBatch(false);
    this.#cancelled = false;
    for (const decision of decisions) {
      if (decision.action === "skip") continue;
      const offer = this.#manifest.files.find((file) => file.id === decision.id);
      if (!offer) continue;
      const handle = await directory.getFileHandle(decision.finalName, { create: true });
      this.#files.set(offer.id, { offer, decision, handle, hash: sha256.create(), offset: 0, pendingChunks: [], pendingBytes: 0 });
    }
    sendControl(this.#channel, { type: "decision", decisions });
  }

  async cancel(reason = "Transfert annulé"): Promise<void> {
    this.#cancelled = true;
    for (const state of this.#files.values()) {
      if (state.flushTimer) clearTimeout(state.flushTimer);
      state.pendingChunks = [];
      state.pendingBytes = 0;
      await state.writer?.abort().catch(() => undefined);
    }
    if (this.#channel?.readyState === "open") sendControl(this.#channel, { type: "cancel", reason });
  }

  async resetBatch(clearManifest = true): Promise<void> {
    for (const state of this.#files.values()) {
      if (state.flushTimer) clearTimeout(state.flushTimer);
      state.pendingChunks = [];
      state.pendingBytes = 0;
      if (state.writer) await state.writer.abort().catch(() => undefined);
    }
    this.#files.clear();
    this.#activeFileId = undefined;
    this.#cancelled = false;
    if (clearManifest) this.#manifest = undefined;
  }

  async #handleMessage(data: unknown): Promise<void> {
    if (typeof data !== "string") {
      if (!(data instanceof ArrayBuffer) || !this.#activeFileId) throw new Error("Bloc de fichier inattendu");
      const state = this.#files.get(this.#activeFileId);
      if (!state || !state.writer) throw new Error("Fichier de destination indisponible");
      const bytes = new Uint8Array(data);
      state.pendingChunks.push(bytes);
      state.pendingBytes += bytes.byteLength;
      if (state.pendingBytes >= writeBatchSize) await this.#flush(state);
      else this.#scheduleFlush(state);
      return;
    }
    const message = this.#controlDecoder.decode(data);
    if (!message) return;
    if (message.type === "manifest") {
      if (this.#manifest?.transferId !== message.manifest.transferId) await this.resetBatch();
      this.#manifest = message.manifest;
      this.onManifest?.(message.manifest);
    } else if (message.type === "file-start") {
      const state = this.#files.get(message.fileId);
      if (!state) throw new Error("Fichier non autorisé");
      if (!state.writer) state.writer = await state.handle.createWritable({ keepExistingData: false });
      if (message.offset !== state.offset) {
        sendControl(this.#channel!, { type: "resume", offsets: { [message.fileId]: state.offset } });
        return;
      }
      this.#activeFileId = message.fileId;
    } else if (message.type === "file-end") {
      const state = this.#files.get(message.fileId);
      if (!state?.writer) throw new Error("Fin de fichier inattendue");
      await this.#flush(state);
      if (state.offset !== state.offer.size) throw new Error("Le fichier reçu est incomplet");
      const digest = bytesToHex(state.hash.digest());
      const expectedDigest = message.sha256 ?? state.offer.sha256;
      if (!expectedDigest || digest !== expectedDigest) {
        await state.writer.abort();
        sendControl(this.#channel!, { type: "error", code: "hash-mismatch", message: `Le contrôle d’intégrité de ${state.offer.name} a échoué.` });
        throw new Error(`Le contrôle d’intégrité de ${state.offer.name} a échoué.`);
      }
      await state.writer.close();
      state.writer = undefined;
      this.#activeFileId = undefined;
      sendControl(this.#channel!, { type: "file-complete", fileId: state.offer.id });
      this.onProgress?.({ fileId: state.offer.id, transferred: state.offer.size, total: state.offer.size, completed: true });
    } else if (message.type === "transfer-complete") {
      this.onComplete?.();
    } else if (message.type === "cancel" || message.type === "error") {
      this.#cancelled = true;
      this.onError?.(message.type === "error" ? message.message : message.reason ?? "Transfert annulé");
    }
  }

  #scheduleFlush(state: ReceiveState): void {
    if (state.flushTimer) return;
    state.flushTimer = setTimeout(() => {
      state.flushTimer = undefined;
      this.#writeQueue = this.#writeQueue.then(() => this.#flush(state)).catch((error) => {
        this.onError?.(error instanceof Error ? error.message : "Échec de l’écriture du fichier.");
      });
    }, partialBatchDelayMs);
  }

  async #flush(state: ReceiveState): Promise<void> {
    if (state.flushTimer) clearTimeout(state.flushTimer);
    state.flushTimer = undefined;
    if (state.pendingBytes === 0) return;
    if (!state.writer) throw new Error("Fichier de destination indisponible");
    const batch = new Uint8Array(state.pendingBytes);
    let offset = 0;
    for (const chunk of state.pendingChunks) {
      batch.set(chunk, offset);
      offset += chunk.byteLength;
    }
    state.pendingChunks = [];
    state.pendingBytes = 0;
    await state.writer.write(batch);
    state.hash.update(batch);
    state.offset += batch.byteLength;
    if (this.#channel?.readyState === "open") sendControl(this.#channel, { type: "ack", fileId: state.offer.id, offset: state.offset });
    this.onProgress?.({ fileId: state.offer.id, transferred: state.offset, total: state.offer.size });
  }

  #queueResume(channel: RTCDataChannel): void {
    this.#writeQueue = this.#writeQueue.then(async () => {
      for (const state of this.#files.values()) await this.#flush(state);
      if (this.#channel === channel && channel.readyState === "open") {
        sendControl(channel, { type: "resume", offsets: Object.fromEntries([...this.#files].map(([id, state]) => [id, state.offset])) });
      }
    }).catch((error) => this.onError?.(error instanceof Error ? error.message : "Échec de la reprise."));
  }
}

export async function fileExists(directory: FileSystemDirectoryHandle, name: string): Promise<boolean> {
  try {
    await directory.getFileHandle(name);
    return true;
  } catch (error) {
    if (error instanceof DOMException && error.name === "NotFoundError") return false;
    throw error;
  }
}
